import React, { useRef, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { lockBodyScroll, unlockBodyScroll } from "utils/bodyScrollLock";
import { IconClose } from "../../icons";
import { PortalLayerContext } from "./portalLayer";
import "./Modal.css";

/**
 * The titled modal's own spacing (2026-10-26, owner: the share and delete dialogs read too airy): its body and its
 * actions row, for every dialog in this shell to share rather than set its own.
 */
export const MODAL_BODY = "flex flex-col gap-3 p-4";
export const MODAL_ACTIONS = "flex justify-end gap-2 px-4 pb-4";

const SHARED_PORTAL_Z_INDEX = 99999;

// Singleton portal container
let portalRoot: HTMLDivElement | null = null;

const getPortalRoot = () => {
  if (!portalRoot) {
    portalRoot = document.createElement("div");
    portalRoot.id = "modal-portal-root";
    portalRoot.style.cssText = `position: fixed; top: 0; left: 0; z-index: ${SHARED_PORTAL_Z_INDEX}; pointer-events: none;`;
    document.body.appendChild(portalRoot);
  }

  return portalRoot;
};

// An OPT-IN escape from the shared root, for a modal that has to paint above a full-screen overlay which
// is itself a direct child of <body> at a z-index higher than the root's 99999 — `ImageDetailPage` at
// `z-[100000]` when `AssetsPickerModal` raises it. The root is `position: fixed`, so it forms a stacking
// context: every modal inside it is capped at 99999 against body-level siblings no matter what class it
// carries, which is why raising the modal's own `className` cannot fix that case. A modal that passes
// `portalZIndex` gets its own container at that z instead.
const createDedicatedRoot = (zIndex: number) => {
  const el = document.createElement("div");
  el.style.cssText = `position: fixed; top: 0; left: 0; z-index: ${zIndex}; pointer-events: none;`;

  return el;
};

export interface ModalProps {
  className?: string;
  contentClassName?: string;
  footer?: JSX.Element;
  children: React.ReactNode;
  title?: string;
  onClose: () => void;
  show: boolean;
  bodyClassName?: string;
  modalTitle?: React.ReactNode;
  /** Rendered before the title in the header — e.g. a back-arrow button of a multi-step dialog. */
  headerStart?: React.ReactNode;
  backdropDisabled?: boolean;
  style?: React.CSSProperties;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  closeButtonAriaLabel?: string;
  autoFocus?: boolean;
  enableFocusTrap?: boolean;
  showCloseButton?: boolean;
  // Render into a dedicated portal container at this z-index instead of the shared `#modal-portal-root`
  // (fixed at 99999). Only needed to clear a body-level overlay above 99999 — see `createDedicatedRoot`.
  portalZIndex?: number;
  aboveDetail?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  className,
  contentClassName,
  children,
  show,
  onClose,
  backdropDisabled = false,
  bodyClassName,
  style,
  ariaLabel = "Modal dialog",
  ariaDescribedBy,
  closeButtonAriaLabel = "Close modal",
  autoFocus = true,
  enableFocusTrap = true,
  showCloseButton = false,
  title,
  headerStart,
  portalZIndex: requestedPortalZIndex,
  aboveDetail = false,
  ...restProps
}) => {
  const portalZIndex = requestedPortalZIndex ?? (aboveDetail ? 100001 : undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Built during render because `createPortal` needs the node immediately; it is attached (and detached)
  // by the effect below. Rendering into a not-yet-attached node is fine — React fills it in, and the
  // content appears the moment the effect appends it.
  const dedicatedRoot = useMemo(() => (portalZIndex === undefined ? null : createDedicatedRoot(portalZIndex)), [portalZIndex]);

  useEffect(() => {
    if (!dedicatedRoot) return;
    document.body.appendChild(dedicatedRoot);

    return () => dedicatedRoot.remove();
  }, [dedicatedRoot]);

  // Body scroll lock — reference-counted so stacked/nested modals don't corrupt each other's
  // saved state (see utils/bodyScrollLock).
  useEffect(() => {
    if (!show) return;

    lockBodyScroll();

    return unlockBodyScroll;
  }, [show]);

  // Focus management
  useEffect(() => {
    if (!show || !autoFocus) return;

    // Store currently focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Focus first focusable element or container
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const focusableElements = containerRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length > 0) {
          (focusableElements[0] as HTMLElement).focus();
        } else {
          containerRef.current.focus();
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      // Restore focus to previous element
      if (previousActiveElement.current && previousActiveElement.current.focus) {
        previousActiveElement.current.focus();
      }
    };
  }, [show, autoFocus]);

  // Focus trap
  useEffect(() => {
    if (!show || !enableFocusTrap) return;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !containerRef.current) return;

      const focusableElements = containerRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTabKey);

    return () => document.removeEventListener("keydown", handleTabKey);
  }, [show, enableFocusTrap]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!show) return;

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [show, handleEscape]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (e.target === e.currentTarget && !backdropDisabled) {
        onClose();
      }
    },
    [backdropDisabled, onClose]
  );

  if (!show) return null;

  const modalClasses = clsx("modal", "outline-none", bodyClassName);
  const backdropClasses = clsx("modalbase", className);

  const modalContent = (
    <div className={backdropClasses} onClick={handleBackdropClick} role="presentation" style={{ pointerEvents: "auto" }}>
      <div ref={containerRef} role="dialog" aria-modal="true" aria-label={ariaLabel} aria-describedby={ariaDescribedBy} className={modalClasses} style={style} tabIndex={-1} {...restProps}>
        <div className={clsx("modal-content", contentClassName)}>
          {/* Title */}
          {title && (
            <div className="sticky top-0 z-10 bg-black flex items-center justify-between gap-3 px-4 py-2.5 border-b border-black-300">
              <div className="flex min-w-0 items-center gap-2">
                {headerStart}
                <h6 className="text-h6 text-white">{title}</h6>
              </div>
              {showCloseButton && (
                <button
                  type="button"
                  className="flex-center flex-shrink-0 h-8 w-8 rounded-full text-grey hover:bg-black-200 hover:text-white transition-colors outline-none"
                  onClick={onClose}
                  aria-label={closeButtonAriaLabel}
                >
                  <IconClose className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Body */}
          {children}

          {/* Footer */}
          {restProps.footer && <div className="sticky bottom-0 z-10 bg-black px-5 py-3 border-t border-black-300">{restProps.footer}</div>}
        </div>

        {/* Close button when no title */}
        {showCloseButton && !title && (
          <button
            type="button"
            className="z-10 absolute top-3 right-3 h-9 w-9 flex-center rounded-full text-grey hover:bg-black-200 hover:text-white transition-colors outline-none"
            onClick={onClose}
            aria-label={closeButtonAriaLabel}
          >
            <IconClose className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  // Publish this modal's layer so body-portaled panels inside it (Select, MultiSelect) open above it.
  return ReactDOM.createPortal(<PortalLayerContext.Provider value={portalZIndex ?? SHARED_PORTAL_Z_INDEX}>{modalContent}</PortalLayerContext.Provider>, dedicatedRoot ?? getPortalRoot());
};

export default React.memo(Modal);
